/* Production speed selector. Adds/removes a £45 "Express Production" fee line
   when the customer toggles Express vs Standard, and fills auto-calculated
   delivery dates (calendar days). Updates the cart in place (no page reload).
   Self-guards against double init. */
(() => {
  if (window.__bsgProdSpeedInit) return;
  window.__bsgProdSpeedInit = true;

  const WEEKDAY = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const MONTH = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  // Add N calendar days to today (weekends are NOT skipped).
  function addWorkingDays(days) {
    const d = new Date();
    d.setDate(d.getDate() + days);
    return d;
  }

  function formatDate(d) {
    return `${WEEKDAY[d.getDay()]}, ${MONTH[d.getMonth()]} ${d.getDate()}`;
  }

  function fillDates(root) {
    const exDays = parseInt(root.dataset.expressDays, 10) || 3;
    const stDays = parseInt(root.dataset.standardDays, 10) || 7;
    const exEl = root.querySelector('[data-prodspeed-date="express"]');
    const stEl = root.querySelector('[data-prodspeed-date="standard"]');
    if (exEl) exEl.textContent = formatDate(addWorkingDays(exDays));
    if (stEl) stEl.textContent = formatDate(addWorkingDays(stDays));
  }

  async function getCart() {
    const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    return res.ok ? res.json() : { items: [] };
  }

  async function removeExpressLines(cart) {
    const lines = (cart.items || []).filter((it) => it.sku === 'EXPRESS-PROD');
    for (const line of lines) {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: line.key, quantity: 0 }),
      });
    }
  }

  async function addExpress(variantId) {
    await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
    });
  }

  // Refresh the cart drawer + cart page sections IN PLACE (no page reload),
  // reusing the theme's own cart-items / cart-drawer refresh methods.
  function refreshCart() {
    let refreshed = false;
    // 1) Cart page or drawer: the <cart-items> custom element knows how to
    //    re-render its sections from the server.
    const cartItems = document.querySelector('cart-items');
    if (cartItems && typeof cartItems.refresh === 'function') {
      try { cartItems.refresh(); refreshed = true; } catch (e) { /* fall through */ }
    }
    // 2) Cart drawer wrapper (updates totals, free-shipping bar, etc.).
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && typeof cartDrawer.refresh === 'function') {
      try { cartDrawer.refresh(true); refreshed = true; } catch (e) { /* fall through */ }
    }
    // 3) Let the theme update the header count bubble + anything listening.
    document.documentElement.dispatchEvent(new CustomEvent('on:cart:add', { bubbles: true }));
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    // 4) Absolute last resort (only if the theme exposed no refreshable cart).
    if (!refreshed) { location.reload(); }
  }

  async function onChange(root) {
    const variantId = root.dataset.expressVariant;
    const selected = root.querySelector('input[name="bsg-prodspeed"]:checked');
    if (!selected) return;
    const wantExpress = selected.value === 'express';

    if (wantExpress && !variantId) {
      // No express product configured — do nothing (Standard remains).
      return;
    }

    root.classList.add('is-loading');
    try {
      const cart = await getCart();
      const hasExpress = (cart.items || []).some((it) => it.sku === 'EXPRESS-PROD');
      if (wantExpress && !hasExpress) {
        await removeExpressLines(cart); // safety: clear any stale, then add one
        await addExpress(variantId);
        refreshCart();
      } else if (!wantExpress && hasExpress) {
        await removeExpressLines(cart);
        refreshCart();
      } else {
        root.classList.remove('is-loading');
      }
    } catch (e) {
      root.classList.remove('is-loading');
    }
  }

  function init(root) {
    // Fill dates if not already done (or still showing the "—" placeholder).
    const ex = root.querySelector('[data-prodspeed-date="express"]');
    const st = root.querySelector('[data-prodspeed-date="standard"]');
    const needsDates = (ex && ex.textContent.trim() === '—') || (st && st.textContent.trim() === '—');
    if (!root.__psDates || needsDates) {
      root.__psDates = true;
      fillDates(root);
    }
  }

  function boot(scope) {
    (scope || document).querySelectorAll('production-speed').forEach(init);
  }

  // Delegated change handler — survives section re-renders (no page reload).
  document.addEventListener('change', (e) => {
    const input = e.target;
    if (!input || input.name !== 'bsg-prodspeed') return;
    const root = input.closest('production-speed');
    if (root) onChange(root);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot());
  } else {
    boot();
  }
  // Re-fill dates whenever the cart/drawer re-renders in place.
  ['cart:refresh', 'on:cart:add', 'on:line-item:change', 'on:cart:change'].forEach((ev) =>
    document.addEventListener(ev, () => boot()));
  document.addEventListener('shopify:section:load', (e) => boot(e.target));
  // Drawer open: theme toggles an [open] attribute / dispatches events.
  document.addEventListener('on:cart-drawer:open', () => boot());
  document.addEventListener('click', (e) => {
    // When the cart drawer toggle is clicked, the drawer content shows shortly after.
    if (e.target.closest('[href="#cart-drawer"], [aria-controls="cart-drawer"], .js-cart-drawer-toggle')) {
      setTimeout(() => boot(), 50);
      setTimeout(() => boot(), 300);
    }
  });
  // Safety net: a few delayed boots after load for drawers that render late.
  [200, 600, 1200].forEach((t) => setTimeout(() => boot(), t));
  // Observe DOM for newly injected production-speed nodes (guarded to date-fill only).
  if (window.MutationObserver) {
    let scheduled = false;
    const mo = new MutationObserver(() => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => { scheduled = false; boot(); });
    });
    mo.observe(document.documentElement, { childList: true, subtree: true });
  }
})();
